/**
 * The agent's microphone and speaker choice, kept per browser.
 *
 * `localStorage` because a device id is only meaningful to the browser that issued it: Chrome scopes
 * ids to the origin and rotates them when permission is revoked or storage is cleared. Nothing here
 * is server-side. The label rides along with each id purely so a later "is no longer available"
 * notice can name the device the agent remembers choosing.
 *
 * Every read and write is guarded: storage can be unavailable (private windows, blocked site data)
 * and a softphone must still go live without it - on the system default, exactly as before this
 * existed.
 */
export const AUDIO_DEVICE_PREFERENCES_KEY = 'ringbax.softphone.audioDevices'

export const EMPTY_AUDIO_DEVICE_PREFERENCES = Object.freeze({
  inputDeviceId: null,
  inputLabel: '',
  outputDeviceId: null,
  outputLabel: ''
})

function _deviceId(value) {
  return typeof value === 'string' && value ? value : null
}

function _label(value, deviceId) {
  return deviceId && typeof value === 'string' ? value : ''
}

/**
 * Anything unusable - unparseable JSON, the wrong shape, an id that is not a string - is dropped in
 * favour of the system default without a word to the agent. A stored value they cannot see is not
 * something to tell them about.
 */
export function normalizeAudioDevicePreferences(value) {
  if (!value || typeof value !== 'object') {
    return { ...EMPTY_AUDIO_DEVICE_PREFERENCES }
  }

  const inputDeviceId = _deviceId(value.inputDeviceId)
  const outputDeviceId = _deviceId(value.outputDeviceId)

  return {
    inputDeviceId,
    inputLabel: _label(value.inputLabel, inputDeviceId),
    outputDeviceId,
    outputLabel: _label(value.outputLabel, outputDeviceId)
  }
}

export function loadAudioDevicePreferences() {
  try {
    const raw = window.localStorage.getItem(AUDIO_DEVICE_PREFERENCES_KEY)

    return normalizeAudioDevicePreferences(raw ? JSON.parse(raw) : null)
  } catch (error) {
    return { ...EMPTY_AUDIO_DEVICE_PREFERENCES }
  }
}

export function saveAudioDevicePreferences(preferences) {
  try {
    window.localStorage.setItem(
      AUDIO_DEVICE_PREFERENCES_KEY,
      JSON.stringify(normalizeAudioDevicePreferences(preferences))
    )
  } catch (error) {
    // The choice still applies to this session; it just will not survive a reload.
    console.warn('Could not save the softphone audio device choice', error)
  }
}
